import { useEffect, useRef } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { CreateVisitFormV2Values } from '../types/form-data.types';

/**
 * Syncs visit-level `email_opt_in` / `sms_opt_in` with visitor contact info:
 * - Auto-selects when email/phone first becomes present
 * - Unchecks when the user clears all email/phone after previously having contact info
 *
 * Does not change opt-ins on mount if contact info is already present (edit load, step remount),
 * so saved values and a host's manual uncheck are preserved.
 * When `enabled` is false, does nothing (e.g. per-visitor cards in multi-visitor edit).
 */
export const useOptInAutoClear = (
  canOptInEmail: boolean,
  canOptInSms: boolean,
  setValue: UseFormSetValue<CreateVisitFormV2Values>,
  enabled = true,
): void => {
  const hadEmailRef = useRef(canOptInEmail);
  const hadPhoneRef = useRef(canOptInSms);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (canOptInEmail) {
      if (!hadEmailRef.current) {
        setValue('email_opt_in', true);
      }
      hadEmailRef.current = true;
      return;
    }
    if (hadEmailRef.current) {
      setValue('email_opt_in', false);
      hadEmailRef.current = false;
    }
  }, [canOptInEmail, setValue, enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (canOptInSms) {
      if (!hadPhoneRef.current) {
        setValue('sms_opt_in', true);
      }
      hadPhoneRef.current = true;
      return;
    }
    if (hadPhoneRef.current) {
      setValue('sms_opt_in', false);
      hadPhoneRef.current = false;
    }
  }, [canOptInSms, setValue, enabled]);
};
